// Storefront v2 product page (stage 08): size change (M30) swaps the strings Liquid rendered per
// variant, add to cart through /cart/add.js then opens the v2 cart drawer (M31), and the mobile
// sticky add bar (M34). No copy in here.

class AmProduct extends HTMLElement {
  connectedCallback() {
    this.variants = JSON.parse(document.querySelector(`script[data-am-variants="${this.dataset.section}"]`)?.textContent || '[]');
    this.form = this.querySelector('[data-am-product-form]');
    if (!this.form || !this.variants.length) return;
    this.form.addEventListener('change', (e) => {
      if (e.target.matches('[data-am-size]')) this.select(Number(e.target.value), true);
    });
    this.form.addEventListener('submit', (e) => this.add(e));
    const checked = this.form.querySelector('[data-am-size]:checked');
    this.select(Number(checked?.value || this.variants[0].id), false);
    this.sticky();
  }

  set(selector, value, html = false) {
    for (const el of this.querySelectorAll(selector)) {
      if (html) el.innerHTML = value;
      else el.textContent = value;
    }
  }

  select(id, fromUser) {
    const v = this.variants.find((x) => x.id === id);
    if (!v) return;
    this.current = v;
    const apply = () => {
      this.style.setProperty('--paper-ratio', v.ratio);
      this.style.setProperty('--paper-w', v.paperW);
      this.style.setProperty('--scale-w', v.scaleW);
      this.set('[data-am-price]', v.price);
      this.set('[data-am-pack]', v.pack);
      this.set('[data-am-ship]', v.ship);
      this.set('[data-am-paper-note]', v.paperNote);
      this.set('[data-am-band]', v.band);
      // the caption is an _html key; its markup comes from the locale file, its value from Liquid
      this.set('[data-am-scale-caption]', v.scaleCaption, true);
      this.set('[data-am-sticky-label]', v.label);
      this.set('[data-am-sticky-price]', v.price);
      const add = this.querySelector('[data-am-add] .am-btn__label');
      if (add) add.textContent = v.add;
      const addBtn = this.querySelector('[data-am-add]');
      addBtn?.toggleAttribute('disabled', !v.available);
      const art = this.querySelector('.am-pdp__art');
      if (art) art.alt = v.alt;
    };
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (fromUser && !reduced && document.startViewTransition) document.startViewTransition(apply);
    else apply();
    if (fromUser) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', id);
      window.history.replaceState({}, '', url);
    }
  }

  async add(e) {
    e.preventDefault();
    const btn = this.querySelector('[data-am-add]');
    const error = this.querySelector('[data-am-add-error]');
    error?.setAttribute('hidden', '');
    btn?.setAttribute('aria-busy', 'true');
    try {
      const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(this.form),
      });
      if (!res.ok) throw new Error(`add ${res.status}`);
      btn?.removeAttribute('aria-busy');
      await this.added(btn);
      document.dispatchEvent(new CustomEvent('am:cart-added', { detail: { id: this.current?.id } }));
    } catch {
      error?.removeAttribute('hidden');
    } finally {
      btn?.removeAttribute('aria-busy');
    }
  }

  /** M31: "added ✓" (the check draws 300ms), then 250ms later the drawer may open. */
  async added(btn) {
    const label = btn?.querySelector('.am-btn__label');
    if (!label || !btn.dataset.amAdded || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const before = label.textContent;
    const check = Object.assign(document.createElement('span'), { className: 'am-btn__check', textContent: '✓' });
    check.setAttribute('aria-hidden', 'true');
    label.replaceChildren(btn.dataset.amAdded, check);
    await new Promise((r) => setTimeout(r, 550));
    // the drawer covers the button now; put the price label back for when it closes
    setTimeout(() => {
      if (label.contains(check)) label.textContent = this.current?.add || before;
    }, 1200);
  }

  /** M34: under 900px, a bottom bar appears once the main add button scrolls out of view. */
  sticky() {
    const bar = this.querySelector('[data-am-sticky]');
    const main = this.querySelector('[data-am-main-actions]');
    if (!bar || !main || !('IntersectionObserver' in window)) return;
    bar.removeAttribute('hidden');
    const btn = bar.querySelector('[data-am-sticky-add]');
    btn.addEventListener('click', () => this.form.requestSubmit());
    // The root reaches far below the viewport, so "not intersecting" means "scrolled out above".
    // Watching the plain viewport, a jump from below the fold to above it (anchor, restored
    // scroll) never crosses a threshold and the bar would stay away.
    new IntersectionObserver(
      ([entry]) => {
        const show = !entry.isIntersecting;
        bar.classList.toggle('is-shown', show);
        bar.setAttribute('aria-hidden', String(!show));
        btn.tabIndex = show ? 0 : -1;
      },
      { rootMargin: '0px 0px 100000px 0px' }
    ).observe(main);
  }
}

customElements.define('am-product', AmProduct);

/** <am-recs>: fills itself from the recommendations endpoint (section rendering). */
class AmRecs extends HTMLElement {
  async connectedCallback() {
    if (this.children.length || !this.dataset.url) return;
    try {
      const res = await fetch(this.dataset.url);
      if (!res.ok) return;
      const next = new DOMParser().parseFromString(await res.text(), 'text/html').querySelector('am-recs');
      if (next && next.children.length) this.replaceChildren(...next.children);
    } catch {
      /* recommendations are optional */
    }
  }
}

customElements.define('am-recs', AmRecs);
